import { faSearch } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { RouteComponentProps, withRouter } from "react-router-dom";
import { Routes } from "../../../classes/Routes";
import * as searchService from "../../../services/searchService";
import { updateInputHandler } from "../../../shared/utility";
import { ETranslation } from "../../../translations/translation-keys";
import Input, { EInputType, IInputField, IInputOptions } from "../../UI/Input/Input";
import classes from "./Search.module.scss";

enum ESearch {
  search = "search",
}

interface IProps extends RouteComponentProps {
  text: string;
}

enum EKind {
  OfferRequest = "OfferRequest",
  Organization = "Organization",
}

interface ISearchItem {
  value: string;
  label: string;
  kind: EKind;
}

const Search: React.FC<IProps> = ({ text, history, location }) => {
  const { t } = useTranslation();

  const [searchOpen, setSearchOpen] = useState<boolean>(false);
  const containerRef = React.useRef<HTMLDivElement>(null);
  const blurTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);

  const [inputs, setInputs] = useState<IInputField>({
    [ESearch.search]: {
      type: EInputType.reactAsyncSelect,
      placeholder: t(ETranslation.COMMON_SEARCH),
      value: "",
    }
  });

  const closeSearch = React.useCallback(() => {
    if (blurTimeoutRef.current) {
      clearTimeout(blurTimeoutRef.current);
      blurTimeoutRef.current = null;
    }
    setSearchOpen(false);
    setInputs(prev => ({
      ...prev,
      [ESearch.search]: {
        ...prev[ESearch.search],
        value: "",
      },
    }));
  }, []);

  const openSearch = () => {
    setSearchOpen(true);
  };

  const handleBlur = React.useCallback(
    (e?: any) => {
      if (blurTimeoutRef.current) {
        clearTimeout(blurTimeoutRef.current);
      }
      const target = e?.target as Node | null;
      const relatedTarget = e?.relatedTarget as Node | null;

      blurTimeoutRef.current = setTimeout(() => {
        if (
          relatedTarget &&
          containerRef.current &&
          containerRef.current.contains(relatedTarget)
        ) {
          return;
        }
        const activeEl = document.activeElement;
        if (
          containerRef.current &&
          activeEl &&
          activeEl !== document.body &&
          activeEl !== target &&
          containerRef.current.contains(activeEl)
        ) {
          return;
        }
        closeSearch();
      }, 150);
    },
    [closeSearch]
  );

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Escape") {
      closeSearch();
    }
  };

  useEffect(() => {
    if (!searchOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        closeSearch();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [searchOpen, closeSearch]);

  useEffect(() => {
    return () => {
      if (blurTimeoutRef.current) {
        clearTimeout(blurTimeoutRef.current);
      }
    };
  }, []);

  const createInput = (inputName: ESearch, options?: IInputOptions) => {
    const item = inputs[inputName];
    return <Input
      {...item}
      {...options}
      onChange={value => updateInputHandler(inputName, value, setInputs)}
      inputName={inputName}
    />
  };

  useEffect(() => {
    if (searchOpen) closeSearch();
    // eslint-disable-next-line
  }, [location]);

  const searchOption = inputs[ESearch.search].value as ISearchItem;

  useEffect(() => {
    const value = searchOption?.value;
    if (value) {
      const kind = searchOption.kind;
      closeSearch();
      switch (kind) {
        case EKind.OfferRequest:
          history.push(Routes.OFFER_REQUEST(value));
          break;
        case EKind.Organization:
          history.push(Routes.SERVICE_PROVIDER(value));
          break;
        default:
          break;
      }
    }
  }, [searchOption, history, closeSearch]);

  const classNames = [classes.Container];

  if (searchOpen) {
    classNames.push(classes.Open);
  }

  return (
    <div
      ref={containerRef}
      className={classNames.join(" ")}
      onKeyDown={handleKeyDown}
    >
      {!searchOpen && (
        <p onClick={openSearch}>
          <FontAwesomeIcon icon={faSearch} />
          {" "}
          {text}
        </p>
      )}
      {searchOpen && createInput(ESearch.search, { 
        fetchOptions: searchService.search, 
        containerStyles: { width: "100%", margin: 0 },
        returnObject: true,
        autoFocus: true,
        onBlur: handleBlur,
      })}
    </div>
  );
}

export default withRouter(Search);
